import * as React from 'react';

import gql from 'graphql-tag';
import _ from 'lodash';
import { compose } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import { useRouter } from 'js/lib/useRouter';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import withNotFound from 'bundles/common/components/withNotFound';
import { itemsPerPage } from 'bundles/directory-graphql/common/constants';
import LinksContainer from 'bundles/directory-graphql/components/LinksContainer';
import Metatags from 'bundles/directory-graphql/components/MetatagsWithPagination';
import NotFound from 'bundles/directory-graphql/components/NotFound';
import type {
  DirectoryPageCollectionsQuery as DirectoryPageCollectionsQueryData,
  DirectoryPageCollectionsQueryVariables,
} from 'bundles/directory-graphql/components/__generated__/DirectoryPageCollectionsQuery';
import PaginationControls from 'bundles/page/components/cds/PaginationControls';

import _t from 'i18n!nls/directory-graphql';

type DirectoryCollections = {
  __typename: string;
  title: string;
  slug: string;
};

type PropsFromRouter = {
  page: string;
};

type PropsToComponent = PropsFromRouter & {
  collections: Array<DirectoryCollections>;
  total: number;
};

export const Collections = ({ total, collections }: PropsToComponent) => {
  const {
    location: {
      query: { page },
    },
  } = useRouter();

  const additionalSettings = {
    title: _t('Collections Directory'),
    description: _t(
      'The Coursera Collections directory includes links to all of our #{numCollections} collections pages.',
      {
        numCollections: total,
      }
    ),
    disableCrawlerIndexing: true,
    useFollowForDisablingIndexing: true,
  };

  const titledLinks = collections.map(({ title, slug }) => {
    return {
      title: title.trim(),
      link: '/collections/' + slug,
    };
  });

  const currentPage = page ? parseInt(page, 10) : 1;
  const pageCount = Math.ceil(total / itemsPerPage);

  if (!titledLinks.length) {
    return null;
  } else {
    return (
      <div>
        <LinksContainer titledLinks={titledLinks} />
        <PaginationControls
          currentPage={currentPage}
          largeStyle
          pageCount={pageCount}
          maxPages={4}
          renderLinks={true}
        />
        <Metatags total={total} additionalSettings={additionalSettings} />
      </div>
    );
  }
};

export default compose<PropsToComponent, {}>(
  connectToRouter((router) => ({ page: router.location.query.page })),
  waitForGraphQL<PropsToComponent, DirectoryPageCollectionsQueryData, DirectoryPageCollectionsQueryVariables, {}>(
    gql`
      query DirectoryPageCollectionsQuery($start: String!, $limit: Int!) {
        CatalogCollectionPagesV1Resource {
          visibleToLearners(start: $start, limit: $limit) {
            collections: elements {
              slug
              title
            }
            paging {
              total
            }
          }
        }
      }
    `,
    {
      props: ({ data }) => {
        const visibleToLearners = data?.CatalogCollectionPagesV1Resource?.visibleToLearners;
        const collections = visibleToLearners?.collections || [];
        const total = visibleToLearners?.paging?.total || 0;

        return {
          collections,
          total,
        };
      },
      options({ page = '1' }) {
        const start = (parseInt(page, 10) - 1) * itemsPerPage;
        return {
          variables: {
            start: start.toString(),
            limit: itemsPerPage,
          },
        };
      },
    }
  ),
  withNotFound({
    test: ({ page = '1', total }) => {
      const pageInt = parseInt(page, 10);
      return _.isNaN(pageInt) || pageInt < 1 || pageInt > Math.ceil(total / itemsPerPage);
    },
    errorComponent: NotFound,
  })
)(Collections);
